import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { isSafePostLoginPath, rememberPostLoginPath } from '@/lib/post-login';
import { useRustyState } from '../../state';

/** Signed-out visits to the account area go to sign-in with `next` set to where they were headed. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const isLoggedIn = useRustyState((s) => s.api?.auth?.getSession().isLoggedIn ?? false);
  const location = useLocation();
  const { createLanguageURL } = useLanguageAwareRouting();

  if (!isLoggedIn) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`${createLanguageURL('/login')}?next=${next}`} replace />;
  }
  return children;
}

/**
 * Keeps `next` from the sign-in URL for whichever flow finishes the sign-in.
 * Someone already signed in goes straight to `next`, or to the account home.
 */
export function RememberNext({ children }: { children: ReactNode }) {
  const [params] = useSearchParams();
  const next = params.get('next');
  const isLoggedIn = useRustyState((s) => s.api?.auth?.getSession().isLoggedIn ?? false);
  const { createLanguageURL } = useLanguageAwareRouting();

  useEffect(() => {
    if (!isLoggedIn) rememberPostLoginPath(next);
  }, [next, isLoggedIn]);

  if (isLoggedIn) {
    return (
      <Navigate to={isSafePostLoginPath(next) ? next : createLanguageURL('/account')} replace />
    );
  }
  return children;
}

/** Old path to new path, keeping the language prefix and the old query; `:name` segments in `to` are filled from the old path. */
export function RedirectTo({ to, search }: { to: string; search?: string }) {
  const params = useParams();
  const location = useLocation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const path = to.replace(/:([A-Za-z]+)/g, (_, name: string) =>
    encodeURIComponent(params[name] ?? '')
  );
  const query = new URLSearchParams(location.search);
  for (const [key, value] of new URLSearchParams(search ?? '')) query.set(key, value);
  const qs = query.toString();
  return <Navigate to={`${createLanguageURL(path)}${qs ? `?${qs}` : ''}`} replace />;
}

/** Stripe still returns deposits to the old profile path; those belong on Billing. */
export function LegacyProfileRedirect() {
  const location = useLocation();
  const deposit = new URLSearchParams(location.search).has('deposit');
  return <RedirectTo to={deposit ? '/account/billing' : '/account/security'} />;
}
